import { Injectable, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { environment } from '../../environment/environment';
import { SessionsService } from '../modules/besharp-sessions/lib/services/sessions.service';
import { buildSessionHeaders } from '../modules/besharp-sessions/lib/utils/interceptor';
import {
  BesharpChatService,
  BesharpChatSendParams,
  BesharpChatStreamEvent,
  BesharpChatHistoryMessage,
  BesharpChatReference,
  BesharpChatQuota,
} from '../modules/besharp-chat/services/besharp-chat.service';

export interface HealthStatus {
  status: string;
  timestamp: string;
  available_models?: string[];
  backend_info?: Record<string, unknown>;
}

export interface SessionInfo {
  session_id: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  first_message?: string;
}

export interface ConversationHistoryMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export type ChatStreamEventType =
  | 'text_chunk'
  | 'tool_call'
  | 'tool_result'
  | 'agent_thinking'
  | 'content_reset'
  | 'error'
  | 'end';

export interface ChatStreamEvent {
  type: ChatStreamEventType;
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

interface StreamSseMessage {
  type?: string;
  content?: unknown;
  error?: string;
  name?: string;
  /** On tool_start/tool_end: the sub-agent that made the call, absent for orchestrator calls. */
  parent?: string;
}

/**
 * The AgentCore Gateway prefixes tool names as `<target>___<tool>` (e.g.
 * `devRetrieverMcp___query_datahub_sql`). The agent strips it on the events it synthesises,
 * but raw names still reach us two ways: the `tool_use` blocks inside `ai_chunk`, and the
 * checkpointed history replayed by `/sessions`. Strip it in both, or the prefix shows up in
 * the reasoning panel and live names stop matching the stripped ones from `tool_start`.
 * Mirrors `base_tool_name` in agents/DatahubAgentCdk/src/tool_names.py.
 */
function baseToolName(name: string): string {
  const parts = name.split('___');
  return parts[parts.length - 1];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** `GET /usage/me` and the `quota` field of a 429 (see docs/token-limits.md). */
interface UsageResponse {
  unlimited: boolean;
  limit: number | null;
  used: number;
  percent: number | null;
  exhausted: boolean;
  resets_at: string;
  // Popover detail: only on GET /usage/me.
  history?: { day: string; percent: number | null; turns: number }[];
}

function toQuota(usage: UsageResponse, previous?: BesharpChatQuota | null): BesharpChatQuota {
  return {
    unlimited: usage.unlimited,
    limit: usage.limit,
    used: usage.used,
    percent: usage.percent,
    exhausted: usage.exhausted,
    resetsAt: usage.resets_at,
    history:
      usage.history?.map((d) => ({ day: d.day, percent: d.percent ?? 0, turns: d.turns })) ?? previous?.history ?? [],
  };
}

function share(credits: number, limit: number): number {
  return Math.round((credits / limit) * 1000) / 10;
}

/** Fires the reset refresh a little after 00:00 UTC, so the new day's counter exists. */
const QUOTA_RESET_GRACE_MS = 5_000;

@Injectable({ providedIn: 'root' })
export class ChatApiService extends BesharpChatService {
  private sessionsService = inject(SessionsService);
  private readonly baseUrl = environment.backend.endpoint || '/api';

  private readonly quotaState = signal<BesharpChatQuota | null>(null);
  override readonly quota = this.quotaState.asReadonly();
  private quotaResetTimer?: ReturnType<typeof setTimeout>;
  private referencesPromise?: Promise<BesharpChatReference[]>;

  /** Emits the new session ID whenever the chat creates a brand-new conversation. */
  readonly newSessionCreated$ = new Subject<string>();

  getBackendInfo(): { url: string; name: string } {
    return { url: this.baseUrl, name: 'ITU Datahub Agent API' };
  }

  async checkHealth(): Promise<HealthStatus> {
    return this.requestJson<HealthStatus>('/ping');
  }

  async listSessions(): Promise<SessionInfo[]> {
    const payload = await this.requestJson<{ sessions: Array<Record<string, unknown>> }>('/sessions');

    return payload.sessions.map((session) => ({
      session_id: String(session['session_id'] ?? ''),
      created_at: String(session['created_at'] ?? ''),
      updated_at: String(session['updated_at'] ?? session['created_at'] ?? ''),
      message_count: Number(session['message_count'] ?? 0),
      first_message: session['first_message'] ? String(session['first_message']) : undefined,
    }));
  }

  async deleteSession(sessionId: string): Promise<{ message: string; session_id: string }> {
    return this.requestJson<{ message: string; session_id: string }>(`/session/${sessionId}/delete`, {
      method: 'DELETE',
    });
  }

  override async getConversationHistory(sessionId?: string): Promise<BesharpChatHistoryMessage[]> {
    if (!sessionId) {
      return [];
    }

    const payload = await this.requestJson<unknown>(`/session/${sessionId}`);
    const rawItems = Array.isArray(payload)
      ? payload
      : isRecord(payload) && Array.isArray(payload['messages'])
        ? payload['messages']
        : [];

    const messages: BesharpChatHistoryMessage[] = [];
    let lastAssistantMessage: BesharpChatHistoryMessage | undefined;
    // Queue of tool-call IDs emitted by the most recent AI turn; consumed in
    // order as each subsequent 'tool' result message is processed.
    const pendingToolCallIds: string[] = [];

    for (const rawItem of rawItems) {
      if (!isRecord(rawItem)) {
        continue;
      }

      const timestamp = new Date().toISOString();
      const type = rawItem['type'];

      if (type === 'human') {
        const content = typeof rawItem['content'] === 'string' ? rawItem['content'] : '';
        messages.push({ role: 'user', content, timestamp });
        lastAssistantMessage = undefined;
        continue;
      }

      if (type === 'ai') {
        if (Array.isArray(rawItem['content'])) {
          const blocks = rawItem['content'] as unknown[];
          const textContent = blocks
            .filter((b): b is Record<string, unknown> => isRecord(b) && b['type'] === 'text')
            .map((b) => (typeof b['text'] === 'string' ? b['text'] : ''))
            .join('');
          const toolUseBlocks = blocks.filter(
            (b): b is Record<string, unknown> => isRecord(b) && b['type'] === 'tool_use'
          );

          if (toolUseBlocks.length > 0) {
            if (!lastAssistantMessage) {
              lastAssistantMessage = { role: 'assistant', content: '', timestamp };
              messages.push(lastAssistantMessage);
            }
            if (textContent) {
              lastAssistantMessage.reasoningSteps = [
                ...(lastAssistantMessage.reasoningSteps ?? []),
                { type: 'agent_thinking', content: textContent, timestamp },
              ];
            }
            for (const block of toolUseBlocks) {
              const toolName = typeof block['name'] === 'string' ? baseToolName(block['name']) : 'tool';
              const toolInput = typeof block['input'] === 'string' ? block['input'] : undefined;
              const toolCallId = typeof block['id'] === 'string' ? (block['id'] as string) : undefined;
              if (toolCallId) pendingToolCallIds.push(toolCallId);
              lastAssistantMessage.reasoningSteps = [
                ...(lastAssistantMessage.reasoningSteps ?? []),
                {
                  type: 'tool_call',
                  content: `Calling ${toolName}...`,
                  timestamp,
                  metadata: { tool_name: toolName, tool_input: toolInput, toolCallId },
                },
              ];
            }
          } else if (textContent) {
            if (lastAssistantMessage && !lastAssistantMessage.content) {
              lastAssistantMessage.content = textContent;
            } else {
              lastAssistantMessage = { role: 'assistant', content: textContent, timestamp };
              messages.push(lastAssistantMessage);
            }
          }
          continue;
        }

        const content = typeof rawItem['content'] === 'string' ? rawItem['content'] : '';
        if (!content) {
          if (!lastAssistantMessage) {
            lastAssistantMessage = { role: 'assistant', content: '', timestamp };
            messages.push(lastAssistantMessage);
          }
        } else if (lastAssistantMessage && !lastAssistantMessage.content) {
          lastAssistantMessage.content = content;
        } else {
          lastAssistantMessage = { role: 'assistant', content, timestamp };
          messages.push(lastAssistantMessage);
        }
        continue;
      }

      if (type === 'tool') {
        if (!lastAssistantMessage) {
          lastAssistantMessage = { role: 'assistant', content: '', timestamp };
          messages.push(lastAssistantMessage);
        }

        const toolName =
          isRecord(rawItem['metadata']) && typeof rawItem['metadata']['name'] === 'string'
            ? baseToolName(rawItem['metadata']['name'])
            : undefined;

        // Each 'tool' message corresponds to exactly one tool call; dequeue
        // the matching ID so all result steps carry the right toolCallId.
        const currentToolCallId = pendingToolCallIds.shift();

        if (Array.isArray(rawItem['content'])) {
          for (const block of rawItem['content'] as unknown[]) {
            if (!isRecord(block) || block['type'] !== 'text') {
              continue;
            }
            const rawText = typeof block['text'] === 'string' ? block['text'] : '';
            const parsed = this.tryParseJson(rawText);
            lastAssistantMessage.reasoningSteps = [
              ...(lastAssistantMessage.reasoningSteps ?? []),
              {
                type: 'tool_result',
                content: this.formatToolContent(parsed, rawText),
                timestamp,
                metadata: { tool_name: toolName, toolCallId: currentToolCallId, ...(parsed ?? {}) },
              },
            ];
          }
          continue;
        }

        const toolContent =
          typeof rawItem['content'] === 'string' && rawItem['content'].length > 0
            ? rawItem['content']
            : 'Tool output received.';
        const parsedTools = this.parseToolPayloads(toolContent);

        if (parsedTools.length === 0) {
          lastAssistantMessage.reasoningSteps = [
            ...(lastAssistantMessage.reasoningSteps ?? []),
            { type: 'tool_result', content: toolContent, timestamp, metadata: { toolCallId: currentToolCallId } },
          ];
        } else {
          lastAssistantMessage.reasoningSteps = [
            ...(lastAssistantMessage.reasoningSteps ?? []),
            ...parsedTools.map((parsedTool) => ({
              type: 'tool_result' as const,
              content: this.formatToolContent(parsedTool, toolContent),
              timestamp,
              metadata: { tool_name: toolName, toolCallId: currentToolCallId, ...parsedTool },
            })),
          ];
        }
      }
    }

    return messages;
  }

  private parseToolPayloads(raw: string): Record<string, unknown>[] {
    const direct = this.tryParseJson(raw);
    if (direct) {
      return [direct];
    }

    const trimmed = raw.trim();
    if (!trimmed) {
      return [];
    }

    const normalizedAsArray = `[${trimmed.replace(/}\s*{/g, '},{')}]`;
    const normalizedParsed = this.tryParseJsonArray(normalizedAsArray);
    if (normalizedParsed.length > 0) {
      return normalizedParsed;
    }

    const chunks = trimmed
      .split(/\n(?=\s*\{)/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const parsedChunks = chunks
      .map((chunk) => this.tryParseJson(chunk))
      .filter((chunk): chunk is Record<string, unknown> => !!chunk);

    return parsedChunks;
  }

  private tryParseJson(raw: string): Record<string, unknown> | undefined {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return isRecord(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  private tryParseJsonArray(raw: string): Record<string, unknown>[] {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed.filter((item): item is Record<string, unknown> => isRecord(item));
    } catch {
      return [];
    }
  }

  private formatToolContent(parsed: Record<string, unknown> | undefined, fallback: string): string {
    if (!parsed) {
      return fallback;
    }

    const text = typeof parsed['text'] === 'string' ? parsed['text'] : undefined;

    if (!text) {
      return fallback;
    }

    return text;
  }

  override async *sendMessageStream(params: BesharpChatSendParams): AsyncGenerator<BesharpChatStreamEvent> {
    const authHeaders = await buildSessionHeaders(this.sessionsService);
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...authHeaders,
    });

    const threadId =
      params.sessionId && params.sessionId.length >= 33
        ? params.sessionId
        : '00000000-0000-0000-0000-000000000000-default';

    const response = await fetch(`${this.baseUrl}/invocations`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        input: { prompt: params.message },
        config: {
          model: params.model,
          configurable: {
            thread_id: threadId,
            actor_id: 'default',
          },
        },
      }),
    });

    if (response.status === 429 || response.status === 503) {
      // Refused before the agent ran: no tokens spent, the prompt was not sent.
      const body = (await response.json().catch(() => ({}))) as {
        message?: string;
        errorCode?: string;
        quota?: UsageResponse;
      };
      if (body.errorCode === 'QUOTA_EXCEEDED') {
        if (body.quota) this.setQuota(toQuota(body.quota, this.quotaState()));
        yield {
          type: 'quota_exceeded',
          content: body.message ?? 'Daily usage quota reached',
          timestamp: new Date().toISOString(),
        };
        return;
      }
      if (body.errorCode === 'QUOTA_UNAVAILABLE') {
        yield {
          type: 'quota_unavailable',
          content: 'Usage service temporarily unavailable — please try again in a minute.',
          timestamp: new Date().toISOString(),
        };
        return;
      }
    }

    if (response.status === 401 || response.status === 403) {
      throw new Error('Unauthorized - please sign in again');
    }

    if (!response.ok || !response.body) {
      throw new Error(`Request failed (${response.status})`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let pendingContent = '';
    const pendingToolNames = new Map<number, string>();
    const pendingToolInputs = new Map<number, string>();
    const pendingToolIds = new Map<number, string>();
    // Gap instrumentation: measured after the ping early-return, so it reports the silence
    // the heartbeat is covering rather than the heartbeat itself. Mirrors the agent-side
    // [gap] logs. The number that matters is CloudFront's 60s origin read timeout.
    const GAP_LOG_THRESHOLD_MS = 10_000;
    let lastFrameAt = 0;

    try {
      while (true) {
        const { done, value } = await reader.read();
        const chunk = decoder.decode(value ?? new Uint8Array(), { stream: !done });
        buffer += chunk;

        const lines = buffer.split('\n');
        buffer = done ? '' : (lines.pop() ?? '');

        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line.startsWith('data:')) {
            continue;
          }

          const payload = line.slice(5).trim();
          if (!payload || payload === '[DONE]') {
            continue;
          }

          let parsed: StreamSseMessage;
          try {
            parsed = JSON.parse(payload) as StreamSseMessage;
          } catch {
            continue;
          }

          const frameAt = performance.now();
          if (lastFrameAt && frameAt - lastFrameAt >= GAP_LOG_THRESHOLD_MS) {
            console.warn(`[gap] ${((frameAt - lastFrameAt) / 1000).toFixed(1)}s before ${parsed.type}`);
          }
          lastFrameAt = frameAt;

          const timestamp = new Date().toISOString();

          // A sub-agent's own tokens. They belong to the reasoning panel, so they arrive
          // under their own type and are deliberately kept out of `pendingContent` and the
          // pendingTool* maps — the orchestrator and a sub-agent both number their content
          // blocks from 0, and mixing them corrupts the tool inputs those maps accumulate.
          if (parsed.type === 'sub_agent_chunk' && typeof parsed.content === 'string') {
            yield {
              type: 'sub_agent_thinking',
              content: parsed.content,
              timestamp,
              metadata: { sub_agent: parsed.name ?? 'sub_agent' },
            };
            continue;
          }

          if (parsed.type === 'ai_chunk' && Array.isArray(parsed.content)) {
            for (const block of parsed.content) {
              if (!isRecord(block)) {
                continue;
              }

              if (block['type'] === 'text' && typeof block['text'] === 'string') {
                pendingContent += block['text'];
                yield {
                  type: 'text_chunk',
                  content: block['text'],
                  timestamp,
                };
                continue;
              }

              if (block['type'] === 'tool_use' && typeof block['name'] === 'string') {
                const idx = typeof block['index'] === 'number' ? block['index'] : -1;
                if (idx >= 0) {
                  pendingToolNames.set(idx, baseToolName(block['name'] as string));
                  if (typeof block['id'] === 'string') pendingToolIds.set(idx, block['id'] as string);
                }
                if (pendingContent) {
                  yield {
                    type: 'agent_thinking',
                    content: pendingContent,
                    timestamp,
                    metadata: { thinking_content: pendingContent },
                  };
                  yield {
                    type: 'content_reset',
                    content: '',
                    timestamp,
                  };
                  pendingContent = '';
                }
              }

              if (block['type'] === 'tool_use' && typeof block['input'] === 'string' && block['name'] === undefined) {
                const idx = typeof block['index'] === 'number' ? block['index'] : -1;
                if (idx >= 0) {
                  pendingToolInputs.set(idx, (pendingToolInputs.get(idx) ?? '') + (block['input'] as string));
                }
              }
            }
            continue;
          }

          if (parsed.type === 'ai' && typeof parsed.content === 'string') {
            pendingContent += parsed.content;
            yield {
              type: 'text_chunk',
              content: parsed.content,
              timestamp,
            };
            continue;
          }

          if (parsed.type === 'tool_start') {
            const toolName =
              typeof parsed.name === 'string'
                ? parsed.name
                : typeof parsed.content === 'string'
                  ? parsed.content
                  : 'tool';

            let toolInput: string | undefined;
            let toolCallId: string | undefined;
            for (const [idx, name] of pendingToolNames.entries()) {
              if (name === toolName) {
                toolInput = pendingToolInputs.get(idx);
                toolCallId = pendingToolIds.get(idx);
                pendingToolNames.delete(idx);
                pendingToolInputs.delete(idx);
                pendingToolIds.delete(idx);
                break;
              }
            }

            if (pendingContent) {
              yield {
                type: 'agent_thinking',
                content: pendingContent,
                timestamp,
                metadata: { thinking_content: pendingContent },
              };
              yield {
                type: 'content_reset',
                content: '',
                timestamp,
              };
              pendingContent = '';
            }

            yield {
              type: 'tool_call',
              content: `Calling ${toolName}...`,
              timestamp,
              // `parent` is the sub-agent that made this call, or undefined when the
              // orchestrator called the tool itself. The panel groups on it.
              metadata: {
                tool_name: toolName,
                tool_input: toolInput,
                toolCallId,
                parent: typeof parsed.parent === 'string' ? parsed.parent : undefined,
              },
            };
            continue;
          }

          if (parsed.type === 'tool_end' || parsed.type === 'tool_result') {
            // Extract the tool_call_id that links this result to a specific tool_call step.
            // The content may be a serialised ToolMessage (Record with top-level tool_call_id)
            // or an Anthropic-style array where content[0] carries tool_use_id.
            let resultToolCallId: string | undefined;
            if (isRecord(parsed.content)) {
              const c = parsed.content as Record<string, unknown>;
              resultToolCallId =
                typeof c['tool_call_id'] === 'string'
                  ? c['tool_call_id']
                  : typeof c['tool_use_id'] === 'string'
                    ? (c['tool_use_id'] as string)
                    : undefined;
            } else if (Array.isArray(parsed.content) && parsed.content.length > 0 && isRecord(parsed.content[0])) {
              const first = parsed.content[0] as Record<string, unknown>;
              resultToolCallId =
                typeof first['tool_call_id'] === 'string'
                  ? first['tool_call_id']
                  : typeof first['tool_use_id'] === 'string'
                    ? (first['tool_use_id'] as string)
                    : undefined;
            }
            const rawToolResult = this.stringifyToolResult(parsed.content);
            const parsedTools = this.parseToolPayloads(rawToolResult);

            if (parsedTools.length === 0) {
              yield {
                type: 'tool_result',
                content: rawToolResult,
                timestamp,
                metadata: { tool_name: parsed.name, toolCallId: resultToolCallId },
              };
              continue;
            }

            for (const parsedTool of parsedTools) {
              yield {
                type: 'tool_result',
                content: this.formatToolContent(parsedTool, rawToolResult),
                timestamp,
                metadata: {
                  tool_name: parsed.name,
                  toolCallId: resultToolCallId,
                  ...parsedTool,
                },
              };
            }
            continue;
          }

          if (parsed.type === 'stream_error') {
            yield {
              type: 'error',
              content: typeof parsed.error === 'string' ? parsed.error : 'Stream error',
              timestamp,
            };
          }
          if (parsed.type === 'usage' && isRecord(parsed.content)) {
            this.applyUsageEvent(parsed.content);
            continue;
          }
          if (parsed.type === 'trace_id' && typeof parsed.content === 'string') {
            yield { type: 'trace_id', content: parsed.content, timestamp };
            continue;
          }
        }

        if (done) {
          break;
        }
      }
    } finally {
      reader.releaseLock();
    }

    yield {
      type: 'end',
      content: '',
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Fetches the whole `@` catalogue in one request.
   *
   * At ~540 entries it is small enough to filter in the browser, which keeps the
   * typeahead instant and means one request per chat session rather than one per
   * keystroke. Cached in the instance so switching sessions does not refetch.
   */
  override async getReferences(): Promise<BesharpChatReference[]> {
    if (!this.referencesPromise) {
      this.referencesPromise = this.requestJson<{ items: BesharpChatReference[] }>('/references')
        .then((response) => response.items ?? [])
        .catch((error) => {
          // A missing catalogue must never block the composer: the user simply types
          // without mentions, and the agent disambiguates the way it always has.
          console.warn('Reference catalogue unavailable, @ mentions disabled', error);
          this.referencesPromise = undefined;
          return [];
        });
    }
    return this.referencesPromise;
  }

  /** Thumbs up/down on a single agent turn, keyed by the Langfuse trace id it carried. */
  override async refreshQuota(): Promise<void> {
    try {
      this.setQuota(toQuota(await this.requestJson<UsageResponse>('/usage/me'), this.quotaState()));
    } catch {
      // The meter is informational: a failed read leaves the last known state, and the
      // quota is still enforced server-side on the next send.
    }
  }

  /** Live update after every turn, from the agent's `usage` event — no extra request. */
  private applyUsageEvent(content: Record<string, unknown>): void {
    const current = this.quotaState();
    const used = content['day_credits'];
    if (!current || typeof used !== 'number') return;
    // The API may have moved to a new day since the meter was loaded.
    if (typeof content['day'] === 'string' && !current.resetsAt.startsWith(this.nextDay(content['day']))) {
      void this.refreshQuota();
      return;
    }
    const limit = typeof content['limit'] === 'number' ? content['limit'] : current.limit;
    const percent = limit === null ? null : Math.min(100, share(used, limit));
    const today = current.history[current.history.length - 1];
    const turns = typeof content['day_turns'] === 'number' ? content['day_turns'] : (today?.turns ?? 0) + 1;

    this.setQuota({
      ...current,
      used,
      limit,
      unlimited: limit === null,
      percent,
      exhausted: limit !== null && used >= limit,
      history: today ? [...current.history.slice(0, -1), { ...today, percent: percent ?? 0, turns }] : current.history,
    });
  }

  private nextDay(day: string): string {
    const date = new Date(`${day}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + 1);
    return date.toISOString().slice(0, 10);
  }

  private setQuota(quota: BesharpChatQuota): void {
    this.quotaState.set(quota);
    clearTimeout(this.quotaResetTimer);
    const delay = new Date(quota.resetsAt).getTime() - Date.now() + QUOTA_RESET_GRACE_MS;
    // A blocked user sees the composer unlock at reset without reloading the page.
    this.quotaResetTimer = setTimeout(() => void this.refreshQuota(), Math.max(delay, QUOTA_RESET_GRACE_MS));
  }

  override async submitFeedback(traceId: string, value: boolean): Promise<void> {
    await this.requestJson(`/trace/${traceId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ value }),
    });
  }

  private async requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
    const authHeaders = await buildSessionHeaders(this.sessionsService);

    const headers = new Headers(init.headers);
    headers.set('Content-Type', 'application/json');
    for (const [key, value] of Object.entries(authHeaders)) {
      headers.set(key, value);
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    });

    if (response.status === 401 || response.status === 403) {
      throw new Error('Unauthorized - please sign in again');
    }

    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`);
    }

    return (await response.json()) as T;
  }

  private stringifyToolResult(content: unknown): string {
    if (typeof content === 'string') {
      return content;
    }

    if (isRecord(content)) {
      if (Array.isArray(content['content'])) {
        const textBlocks = content['content']
          .filter((item) => isRecord(item) && item['type'] === 'text' && typeof item['text'] === 'string')
          .map((item) => String((item as Record<string, unknown>)['text']));

        if (textBlocks.length > 0) {
          return textBlocks.join('\n\n');
        }
      }

      return JSON.stringify(content);
    }

    return '';
  }
}
